import type { SearchEngineResult } from "../../../modules/url-params/search-engine-params/types";

/**
 * Selects the one rank authority the UI should render.
 *
 * FAST is a provisional result set while SMART is pending and remains available
 * when Deep Search is hidden. After SMART succeeds, its model-approved order is
 * the refined prefix. When SMART selects an item already shown by FAST, the
 * original FAST item occupies that refined position so its provenance and
 * feedback session do not change underneath the user. Any FAST item SMART
 * omitted remains at the tail in its original relative order. Scores from the
 * two engines are never compared.
 */
export const selectSearchResultItems = ({
  fastItems,
  smartItems,
  smartSearchSucceeded,
  showSmartSearchResults,
}: {
  fastItems: SearchEngineResult[];
  smartItems: SearchEngineResult[];
  smartSearchSucceeded: boolean;
  showSmartSearchResults: boolean;
}): SearchEngineResult[] => {
  if (!showSmartSearchResults || !smartSearchSucceeded) {
    return fastItems;
  }

  const fastItemsByIdentity = new Map(
    fastItems.flatMap(item => {
      const identity = searchResultItemIdentity(item);
      return identity === undefined ? [] : [[identity, item] as const];
    })
  );

  const refinedItems = smartItems.map(item => {
    const identity = searchResultItemIdentity(item);
    const existingFastItem = identity === undefined ? undefined : fastItemsByIdentity.get(identity);

    if (existingFastItem) {
      return existingFastItem;
    }

    return {
      ...item,
      isDeepSearch: true,
    };
  });

  const refinedItemIdentities = new Set(
    refinedItems
      .map(searchResultItemIdentity)
      .filter((identity): identity is string => identity !== undefined)
  );

  const preservedFastItems = fastItems.filter(item => {
    const identity = searchResultItemIdentity(item);
    return identity === undefined || !refinedItemIdentities.has(identity);
  });

  const mergedItems = [...refinedItems, ...preservedFastItems];

  let nextOrdinalScore = mergedItems.filter(item => item.kind === "ITEM").length;
  return mergedItems.map(item => {
    if (item.kind !== "ITEM") {
      return item;
    }

    return {
      ...item,
      value: {
        ...item.value,
        score: {
          value: nextOrdinalScore--,
        },
      },
    };
  });
};

const searchResultItemIdentity = (item: SearchEngineResult): string | undefined => {
  if (item.kind !== "ITEM") {
    return undefined;
  }

  if (item.value.kind === "NOTE") {
    return `NOTE:${item.value.value.note_id}`;
  }

  return `COLLECTION:${item.value.value.collection_id}`;
};
